import { FC, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Modal, Typography } from '@mui/material';
import { Field, Formik } from 'formik';
import { Form } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import ReactGA from 'react-ga4';

import LogoIcon from '../../assets/landing/new_logo.png';
import HeroImg from '../../assets/landing/Img_Hero.png';
import CompasImg from '../../assets/landing/compass.png';
import AssignmentImg from '../../assets/landing/AssignmentTurnedIn.png';
import PeopleImg from '../../assets/landing/people.png';
import AutoAwesomeImg from '../../assets/landing/AutoAwesome.png';
import PhoneImg from '../../assets/landing/Img_CTA 3.png';
import InstIcon from '../../assets/landing/inst.png';
import YouTubeIcon from '../../assets/landing/youtube.png';
import ElipseHeroRight from '../../assets/landing/Ellipse_hero.png';
import ElipseHeroLeft from '../../assets/landing/Ellipse_hero_left.png';
import ElipseMidle from '../../assets/landing/Ellipse_midle.png';
import CloudBigImg from '../../assets/landing/cloud/CloudBig.png';
import CloudSmallImg from '../../assets/landing/cloud/CloudSmall.png';
import AirplaneImg from '../../assets/landing/airpane.png';
import LandImg from '../../assets/landing/land.png';
import LandLeftImg from '../../assets/landing/landLeft.png';
import EllipseMidleRightImg from '../../assets/landing/Ellipse_midle_right.png';
import HeroImgMobileImg from '../../assets/landing/Hero_img_mabile.png';
import EllipseHeroMobileImg from '../../assets/landing/mobileBg/EllipseHeroMobile.png';
import EllipseHeroLeftMobileImg from '../../assets/landing/mobileBg/EllipseHeroLeftMobile.png';
import EllipseMidleLeftMobileImg from '../../assets/landing/mobileBg/EllipseMidleLeftMobile.png';
import EllipseEndImg from '../../assets/landing/mobileBg/EllipseEnd.png';
import EllipseMidleImg from '../../assets/landing/mobileBg/EllipseMidleRight.png';

import CustomButton from '../../common/CustomButton';
import { useIsMobile } from '../../hooks/useWindowWidth';
import CustomInput from '../../common/CustomInput';
import { useSubmitLandingMutation } from '../../store/services/landingFormApi';

import styles from './Landing.module.scss';

const benefits = [
  {
    title: 'Start From Scratch',
    description: 'Plan a trip your way—or let AI build you a personalized one from scratch.',
    icon: CompasImg,
    bgIconColor: '#FFF2D9',
  },
  {
    title: 'Fully Organized, On The Go',
    description: 'Flights, stays, plans, and more—always at your fingertips. No digging.',
    icon: AssignmentImg,
    bgIconColor: '#EBFBF4',
  },
  {
    title: 'Smarter Trips, Together',
    description: 'Collaborate on plans or adjust on the go with everyone in sync.',
    icon: PeopleImg,
    bgIconColor: '#FDEFEB',
  },
  {
    title: 'Intelligent Travel, Built In',
    description: 'Plan updates, packing lists, flight delay predictions—AI handles it all and more!',
    icon: AutoAwesomeImg,
    bgIconColor: '#F2F4FC',
  },
];

const Landing: FC = () => {
  const [submitLandingMutation, { isSuccess }] = useSubmitLandingMutation();

  const isMobile = useIsMobile();
  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);
  const [isPrivay, setIsPrivacy] = useState(false);
  const [isUpdates, setIsUpdates] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);

  ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS || '');

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const initialValues = {
    name: '',
    email: '',
    features: [] as string[],
  };

  const handleSubmit = async (values: { email: string; name: string; features: string[] }) => {
    if (captcha) {
      try {
        submitLandingMutation({
          name: values.name,
          email: values.email,
          benefits: values.features,
        }).unwrap();

        setResErrors(null);
        setSubmitted(true);
      } catch (error) {
        console.error('Error logging in:', error);
        setResErrors((error as any)?.data?.errors || null);
      }
    } else {
      console.error('ReCAPTCHA reference is null');
      return;
    }
  };

  const formikValidate = (values: { email: string; name: string }) => {
    const errors: { email?: string; name?: string } = {};
    if (!values.name) {
      errors.name = 'Name is required';
    }
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    return errors;
  };

  return (
    <>
      <Box className={styles.landing}>
        <img
          src={ElipseHeroLeft}
          className={styles.bgElipseHeroLeft}
          alt="ElipseHeroLeft"
        />
        <img
          src={ElipseHeroRight}
          className={styles.bgElipseHeroRight}
          alt="ElipseHeroRight"
        />
        <img
          src={ElipseMidle}
          className={styles.bgElipseMidle}
          alt="ElipseMidle"
        />
        <img
          src={CloudBigImg}
          className={styles.bgCloudBigImg}
          alt="CloudBigImg"
        />
        <img
          src={CloudSmallImg}
          className={styles.bgCloudSmallImg}
          alt="CloudSmallImg"
        />
        <img
          src={CloudBigImg}
          className={styles.bgCloudBigImgRightSide}
          alt="CloudBigImg"
        />
        <img
          src={CloudSmallImg}
          className={styles.bgCloudSmallImgRightSide}
          alt="CloudSmallImg"
        />
        <img
          src={AirplaneImg}
          className={styles.bgAirplaneImg}
          alt="AirplaneImg"
        />
        <img
          src={LandImg}
          className={styles.bgLandImg}
          alt="LandImg"
        />
        <img
          src={LandLeftImg}
          className={styles.bgLandLeftImg}
          alt="LandLeftImg"
        />
        <img
          src={EllipseMidleRightImg}
          className={styles.bgEllipseMidleRightImg}
          alt="EllipseMidleRightImg"
        />
        <img
          src={EllipseHeroMobileImg}
          className={styles.bgEllipseHeroMobileImg}
          alt="EllipseHeroMobileImg"
        />
        <img
          src={EllipseHeroLeftMobileImg}
          className={styles.bgEllipseHeroLeftMobileImg}
          alt="EllipseHeroLeftMobileImg"
        />
        <img
          src={EllipseMidleLeftMobileImg}
          className={styles.bgEllipseMidleLeftMobileImg}
          alt="EllipseMidleLeftMobileImg"
        />
        <img
          src={EllipseEndImg}
          className={styles.bgEllipseEndImg}
          alt="EllipseEndImg"
        />
        <img
          src={EllipseMidleImg}
          className={styles.bgEllipseMidleImg}
          alt="EllipseMidleImg"
        />

        <nav className={styles.navbar}>
          <Box>
            <Typography variant="h6Bold">Trav</Typography>
            <img
              src={LogoIcon}
              className={styles.logo}
              alt="logo"
            />
            <Typography variant="h6Bold">Genius</Typography>
          </Box>
          <Box className={styles.navBtnWrapper}>
            <CustomButton
              variant="outlined"
              customClass={styles.navBtn}
              onClick={(e: React.MouseEvent) => handleSmoothScroll(e, '#benefits')}
            >
              <Typography variant="h10Bold">Features</Typography>
            </CustomButton>
            <CustomButton
              variant="outlined"
              customClass={styles.navBtn}
              onClick={(e: React.MouseEvent) => handleSmoothScroll(e, '#form')}
            >
              <Typography variant="h10Bold">Get Early Access</Typography>
            </CustomButton>
          </Box>
        </nav>
        <Box className={styles.heroSection}>
          <Box className={styles.hero}>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: `${isMobile ? '40px' : '64px'}`, lineHeight: '110%' }}
            >
              Your Ultimate
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: `${isMobile ? '40px' : '64px'}`, lineHeight: '110%' }}
              className={styles.gradientText}
            >
              All-In-One
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: `${isMobile ? '40px' : '64px'}`, lineHeight: '110%' }}
            >
              Travel App
            </Typography>

            <Box className={styles.heroText}>
              <Typography
                variant={`${isMobile ? 'h10' : 'h6'}`}
                className={styles.heroSubTitle}
              >
                Helping you plan smarter and explore more—so you can worry less.
              </Typography>
              <CustomButton
                onClick={(e: React.MouseEvent) => handleSmoothScroll(e, '#form')}
                customClass={styles.customButton}
              >
                <Typography variant="h10Bold">Join Now — It’s Free!</Typography>
              </CustomButton>
            </Box>
          </Box>
          <img
            src={HeroImg}
            alt="HeroImg"
            className={styles.heroImage}
          />
          <img
            src={HeroImgMobileImg}
            alt="HeroImgMobileImg"
            className={styles.heroImageMobile}
          />
        </Box>

        <Box
          id="benefits"
          className={styles.benefits}
        >
          <Typography
            variant="h1Bold"
            sx={{ fontSize: `${isMobile ? '36px' : '40px'}`, textAlign: `${isMobile ? 'center' : 'start'}` }}
          >
            What makes TravGenius smarter?
          </Typography>
          <Typography
            variant={`${isMobile ? 'h10' : 'h9'}`}
            sx={{ color: '#373F4F', maxWidth: '634px', width: '100%', textAlign: 'center' }}
          >
            It’s not just a trip planner or trip manager. It’s your always-there do-it-all assistant, co-pilot, and
            travel brain—before and during your journey.
          </Typography>
          <Box className={styles.benefitsCardWrapper}>
            {benefits.map((item, index) => (
              <Box
                key={index}
                className={styles.benefitCard}
              >
                <Box
                  sx={{
                    backgroundColor: item.bgIconColor,
                    borderRadius: '20px',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={item.icon}
                    alt="logo"
                  />
                </Box>
                <Box className={styles.benefitCardText}>
                  <Typography
                    variant="h6Semibold"
                    sx={{ color: '#222B3A', paddingTop: '20px' }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="h10"
                    sx={{ color: '#373F4F' }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={styles.ctaSection}>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={PhoneImg}
              alt="logo"
            />
          </Box>
          <Box className={styles.ctaTextWrapper}>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '56px', lineHeight: '115%' }}
              className={styles.ctaSectionText}
            >
              Get
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '56px', lineHeight: '115%' }}
              className={`${styles.gradientText} ${styles.ctaSectionText}`}
            >
              {' '}
              PREMIUM
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '56px', lineHeight: '115%' }}
              className={styles.ctaSectionText}
            >
              {' '}
              Access to TravGenius
            </Typography>
            <Box className={styles.ctaText}>
              <Typography
                variant="h7"
                sx={{ color: '#373F4F', paddingTop: '20px' }}
              >
                Unlock a full month of Premium features, including AI capabilities—
                <Typography
                  variant="h7Bold"
                  sx={{ color: '#0098F0' }}
                >
                  totally free!
                </Typography>
              </Typography>
              <a
                className={styles.gradientBtn}
                onClick={(e: React.MouseEvent) => handleSmoothScroll(e, '#form')}
              >
                <Typography
                  variant="h10Bold"
                  sx={{ color: '#fff' }}
                >
                  Join Now — It’s Free!
                </Typography>
              </a>
            </Box>
          </Box>
        </Box>

        <Box className={styles.ctaSectionMobile}>
          <Box className={styles.ctaTextWrapper}>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '32px', lineHeight: '115%' }}
              className={styles.ctaSectionText}
            >
              Get
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '32px', lineHeight: '115%' }}
              className={`${styles.gradientText} ${styles.ctaSectionText}`}
            >
              {' '}
              PREMIUM
            </Typography>
            <Typography
              variant="h1Bold"
              sx={{ fontSize: '32px', lineHeight: '115%' }}
              className={styles.ctaSectionText}
            >
              {' '}
              Access to TravGenius
            </Typography>
            <Box className={styles.ctaText}>
              <Typography
                variant="h10"
                sx={{ color: '#373F4F', marginTop: '20px' }}
              >
                Break the Figma limits and explore the endless possibilities with Anima.
              </Typography>
              <a
                className={styles.gradientBtn}
                onClick={(e: React.MouseEvent) => handleSmoothScroll(e, '#form')}
              >
                <Typography
                  variant="h10Bold"
                  sx={{ color: '#fff' }}
                >
                  Join Now — It’s Free!
                </Typography>
              </a>
            </Box>
          </Box>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={PhoneImg}
              alt="logo"
            />
          </Box>
        </Box>

        <Box
          id="form"
          className={styles.formSection}
        >
          <Typography
            variant="h1Bold"
            sx={{ fontSize: `${isMobile ? '36px' : '40px'}` }}
          >
            Limited Time For Insiders!
          </Typography>
          <Typography
            variant={`${isMobile ? 'h10' : 'h9'}`}
            sx={{ color: '#373F4F', maxWidth: '634px', width: '100%', textAlign: 'center' }}
          >
            Get early access + 30 days of premium —
            <Typography
              variant="h7Bold"
              sx={{ color: '#0098F0' }}
            >
              {' '}
              FREE!
            </Typography>
          </Typography>

          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
          >
            {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
              <Form
                className={styles.form}
                onSubmit={handleSubmit}
              >
                <CustomInput
                  inputType="text"
                  inputName="name"
                  inputLabel="Full Name"
                  value={values.name}
                  error={errors.name}
                  customClass={styles.customInput}
                  onChange={handleChange}
                  required
                />
                <CustomInput
                  inputType="email"
                  inputName="email"
                  value={values.email}
                  error={errors.email}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />

                <Box sx={{ marginTop: '18px' }}>
                  <Typography
                    variant="h8Bold"
                    sx={{ maxWidth: '390px', width: '100%', textAlign: 'center', display: 'block', margin: '0 auto' }}
                  >
                    Which app capabilities are you most excited about using?
                  </Typography>
                </Box>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Field
                        type="checkbox"
                        name="features"
                        value="Planning Trips"
                        as={Checkbox}
                      />
                    }
                    label="Planning Trips"
                  />
                  <FormControlLabel
                    control={
                      <Field
                        type="checkbox"
                        name="features"
                        value="Personalized Recommendations"
                        as={Checkbox}
                      />
                    }
                    label="Personalized Recommendations"
                  />
                  <FormControlLabel
                    control={
                      <Field
                        type="checkbox"
                        name="features"
                        value="On-the-Go Trip Organization"
                        as={Checkbox}
                      />
                    }
                    label="On-the-Go Trip Organization"
                  />
                  <FormControlLabel
                    control={
                      <Field
                        type="checkbox"
                        name="features"
                        value="Realtime Trip Collaboration With Friends & Family"
                        as={Checkbox}
                      />
                    }
                    label="Realtime Trip Collaboration With Friends & Family"
                  />
                  <FormControlLabel
                    control={
                      <Field
                        type="checkbox"
                        name="features"
                        value="AI-powered Travel Assistant"
                        as={Checkbox}
                      />
                    }
                    label="AI-powered Travel Assistant"
                  />
                </FormGroup>

                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                  onChange={value => setCaptcha(value)}
                />

                <CustomButton
                  btnType="submit"
                  customClass={styles.customButton}
                  disabled={isSubmitting || !isPrivay || !isUpdates || !captcha || submitted || !!errors.name}
                >
                  Submit
                </CustomButton>

                {isSuccess && (
                  <Typography
                    variant="h12"
                    sx={{ color: '#0098F0', textAlign: 'center', marginTop: '20px' }}
                  >
                    Thank you for signing up! We will notify you when we launch.
                  </Typography>
                )}
                {resErrors &&
                  resErrors.map((error, index) => (
                    <Typography
                      key={index}
                      variant="h12"
                      sx={{ color: '#C8102E' }}
                    >
                      {error.detail}
                    </Typography>
                  ))}
              </Form>
            )}
          </Formik>

          <FormGroup className={styles.formGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  name="terms & conditions"
                  value={isPrivay}
                  onChange={() => setIsPrivacy(!isPrivay)}
                  checked={isPrivay}
                />
              }
              label={
                <Typography
                  variant="h10"
                  sx={{ color: '#5E6A81' }}
                >
                  {'Yes, I agree to the '}
                  <Typography
                    variant="h10Semibold"
                    sx={{ color: '#5E6A81', '&:hover': { textDecoration: 'underline' } }}
                  >
                    <a
                      href="/terms-privacy/TravGenius Terms and Conditions MAIN.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#222B3A' }}
                    >
                      Terms & Conditions
                    </a>
                  </Typography>
                  {' and '}
                  <Typography
                    variant="h10Semibold"
                    sx={{ color: '#5E6A81', '&:hover': { textDecoration: 'underline' } }}
                  >
                    <a
                      href="/terms-privacy/TravGenius Privacy Policy (GDPR & CCPA) MAIN.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#222B3A' }}
                    >
                      Privacy Policy
                    </a>
                  </Typography>
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="features"
                  value={isUpdates}
                  onChange={() => setIsUpdates(!isUpdates)}
                  checked={isUpdates}
                />
              }
              label={
                <Typography
                  variant="h10"
                  sx={{ color: '#5E6A81' }}
                >
                  Yes, I agree to get updates and early access emails
                </Typography>
              }
            />
          </FormGroup>
        </Box>
      </Box>
      <footer className={styles.footerWrapper}>
        <Box className={styles.footer}>
          <Typography
            variant="h10"
            sx={{ color: '#FEFEFE' }}
          >
            © 2025 TravGenius. All rights reserved.
          </Typography>

          <Box className={styles.footerIcons}>
            <a href=" https://www.youtube.com/channel/UCcyOI2xMEfK940Tgwzb2VHg?sub_confirmation=1">
              <img
                src={YouTubeIcon}
                alt="youtube"
              />
            </a>
            <a href="https://www.instagram.com/travgenius.ai">
              <img
                src={InstIcon}
                alt="inst"
              />
            </a>
          </Box>
        </Box>
      </footer>
    </>
  );
};

export default Landing;
